import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Building2, Ruler, Calendar, ShieldCheck, ArrowLeft, Scale, Check, MessageSquare, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PropertyScorecard } from "@/components/PropertyScorecard";
import { VisitorModal } from "@/components/VisitorModal";
import { PropertySearch } from "@/components/PropertySearch";
import { useApp } from "@/context/AppContext";
import { useCompare } from "@/lib/useCompare";
import { formatPriceRange } from "@/lib/format";
import { VERTICALS } from "@/lib/perspective";
import { imgSrc } from "@/lib/api";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, addItem } = useApp();
  const { ids, toggle } = useCompare();
  const property = data.properties.find((p) => p.id === id);
  const [activeImg, setActiveImg] = useState(0);
  const [modal, setModal] = useState(null);

  if (!property) {
    return (
      <div className="container-arch flex min-h-[70vh] flex-col items-center justify-center text-center">
        <h1 className="font-serif text-4xl text-[var(--text-primary)]">{data.properties.length ? "Property not found" : "Loading perspective…"}</h1>
        <Link to="/properties" className="btn-outline-gold mt-6">Back to Properties</Link>
      </div>
    );
  }

  const inCompare = ids.includes(property.id);
  const mapQuery = property.mapQuery || `${property.project}, ${property.location}, Bengaluru`;
  const mapLink = property.mapUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;

  const handleInterest = async (f) => {
    try {
      await addItem("interests", { clientName: f.name, phone: f.phone, email: f.email, clientId: null, propertyId: property.id, propertyName: property.project, stage: "New", advisor: "", createdAt: new Date().toISOString(), notes: f.notes || "Expressed interest from property page" });
      toast.success("Your interest has been shared with Archedia.");
      setModal(null);
    } catch (e) { toast.error("Could not send right now. Please try again."); }
  };

  const handleVisit = async (f) => {
    try {
      await addItem("siteVisits", { clientName: f.name, phone: f.phone, email: f.email, clientId: null, propertyId: property.id, propertyName: property.project, advisor: "", date: new Date(f.date).toISOString(), time: f.time, stage: "Requested", feedback: null, notes: f.notes });
      toast.success("Site visit requested — an advisor will confirm shortly.");
      setModal(null);
    } catch (e) { toast.error("Could not send right now. Please try again."); }
  };

  const handleCompare = () => {
    const res = toggle(property.id);
    if (res.full) toast.error("You can compare up to 3 properties.");
    else toast.success(res.added ? "Added to compare" : "Removed from compare");
  };

  const spec = (icon, label, value) => (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-[var(--accent-gold)]">{icon}</div>
      <div><div className="text-[11px] uppercase tracking-widest text-[var(--text-muted)]">{label}</div><div className="text-sm font-medium text-[var(--text-primary)]">{value}</div></div>
    </div>
  );

  return (
    <div className="pt-24">
      <div className="container-arch">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--accent-gold)]" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="mt-4 max-w-2xl" data-testid="detail-search"><PropertySearch compact placeholder="Search another project, developer or location…" /></div>
      </div>

      {/* Gallery */}
      <div className="container-arch mt-5 grid gap-3 lg:grid-cols-[2fr_1fr]">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="img-zoom overflow-hidden rounded-2xl">
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-[var(--bg-card)]">
            <img src={imgSrc(property.images[activeImg])} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
            <img src={imgSrc(property.images[activeImg])} alt={property.project} className="relative h-full w-full object-contain" data-testid="gallery-main" />
          </div>
        </motion.div>
        <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
          {property.images.map((img, i) => (
            <button key={`${img}-${i}`} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`} className={`img-zoom overflow-hidden rounded-xl border-2 transition ${activeImg === i ? "border-[var(--accent-gold)]" : "border-transparent"}`}>
              <img src={imgSrc(img)} alt="" className="aspect-[4/3] h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      {/* Header + specs */}
      <div className="container-arch mt-8 grid gap-10 lg:grid-cols-[1.5fr_1fr]">
        <div>
          <div className="flex flex-wrap gap-2">
            {property.category.map((c) => <span key={c} className="rounded-full bg-[var(--accent-a15)] px-3 py-1 text-xs font-semibold text-[var(--accent-gold)]">{VERTICALS[c]?.name}</span>)}
          </div>
          <div className="mt-3 overline">Archedia Property Brief</div>
          <h1 className="mt-2 font-serif text-4xl font-light text-[var(--text-primary)] sm:text-5xl">{property.project}</h1>
          <p className="mt-2 flex items-center gap-2 text-[var(--text-muted)]"><Building2 className="h-4 w-4" /> {property.developer} · <MapPin className="h-4 w-4" /> {property.location}, {property.microMarket}</p>
          <p className="mt-6 max-w-2xl leading-relaxed text-[var(--text-muted)]">{property.description}</p>
        </div>

        <div className="arch-card h-fit p-6">
          <div className="text-[11px] uppercase tracking-widest text-[var(--text-muted)]">Price range</div>
          <div className="font-serif text-3xl text-[var(--text-primary)]">{formatPriceRange(property.priceMin, property.priceMax)}</div>
          <div className="my-5 hairline" />
          <div className="grid grid-cols-2 gap-5">
            {spec(<Ruler className="h-4 w-4" />, "Area", property.area)}
            {spec(<Building2 className="h-4 w-4" />, "Configuration", property.configuration.join(", "))}
            {spec(<Calendar className="h-4 w-4" />, "Possession", property.possession)}
            {spec(<Building2 className="h-4 w-4" />, "Stage", property.constructionStage)}
            {spec(<ShieldCheck className="h-4 w-4" />, "RERA", property.rera.slice(0, 18) + "…")}
            {spec(<MapPin className="h-4 w-4" />, "Type", property.propertyType)}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button onClick={() => setModal("interest")} data-testid="cta-interested" className="btn-gold !px-4 !py-3 text-xs">I'm Interested</button>
            <button onClick={() => setModal("visit")} data-testid="cta-visit" className="btn-outline-gold !px-4 !py-3 text-xs">Schedule Site Visit</button>
            <button onClick={handleCompare} data-testid="cta-compare" className="btn-outline-gold !px-4 !py-3 text-xs">{inCompare ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />} Compare</button>
            <Link to="/request" data-testid="cta-requirement" className="btn-outline-gold !px-4 !py-3 text-xs"><MessageSquare className="h-4 w-4" /> Find My Property</Link>
          </div>
          <Link to="/contact" className="mt-3 block text-center text-sm text-[var(--accent-gold)]">Talk to Archedia →</Link>
        </div>
      </div>

      {/* Amenities & Location */}
      <div className="container-arch mt-14 grid gap-8 lg:grid-cols-3">
        <div className="arch-card p-6">
          <h3 className="font-serif text-xl text-[var(--text-primary)]">Amenities</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {property.amenities.map((a) => <span key={a} className="rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-xs text-[var(--text-primary)]">{a}</span>)}
          </div>
        </div>
        <div className="arch-card p-6">
          <h3 className="font-serif text-xl text-[var(--text-primary)]">Location Highlights</h3>
          <ul className="mt-4 space-y-2">
            {property.locationHighlights.map((l) => <li key={l} className="flex items-center gap-2 text-sm text-[var(--text-muted)]"><span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-gold)]" />{l}</li>)}
          </ul>
        </div>
        <div className="arch-card p-6">
          <h3 className="font-serif text-xl text-[var(--text-primary)]">Developer</h3>
          <p className="mt-4 text-sm text-[var(--text-muted)]">{property.developerDetails}</p>
        </div>
      </div>

      {/* Map */}
      <section className="container-arch mt-8" data-testid="property-map-section">
        <div className="arch-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5">
            <div><h3 className="font-serif text-xl text-[var(--text-primary)]">Location on Map</h3><p className="mt-1 text-sm text-[var(--text-muted)]">{mapQuery}</p></div>
            <a href={mapLink} target="_blank" rel="noopener noreferrer" data-testid="open-google-maps" className="btn-gold !px-4 !py-2.5 text-xs"><MapPin className="h-4 w-4" /> Open in Google Maps <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
          <iframe title={`Map of ${property.project}`} src={mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-72 w-full border-0 sm:h-96" data-testid="property-map-embed" />
        </div>
      </section>

      {/* Perspective scorecard */}
      <section className="container-arch mt-20">
        <Reveal><PropertyScorecard property={property} /></Reveal>
      </section>

      <section className="container-arch mt-16 mb-24 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-8 text-center sm:p-12">
        <h3 className="font-serif text-2xl font-light text-[var(--text-primary)] sm:text-3xl">Ready to see this differently?</h3>
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          <button onClick={() => setModal("interest")} className="btn-outline-gold">I'm Interested</button>
          <Link to="/request" className="btn-gold" data-testid="detail-find-my-property">Find My Property</Link>
        </div>
      </section>

      <VisitorModal key={modal} open={modal === "interest"} onClose={() => setModal(null)} title="I'm Interested" subtitle={`Tell us how to reach you about ${property.project}.`} submitLabel="Share Interest" onSubmit={handleInterest} />
      <VisitorModal key={`${modal}-v`} open={modal === "visit"} onClose={() => setModal(null)} title="Schedule a Site Visit" subtitle={`Pick a preferred slot for ${property.project}.`} submitLabel="Request Visit" onSubmit={handleVisit} withDate />
    </div>
  );
}
